import client from './auth-client';
import { Session } from '../models/session';
import { Tokens } from '../models/tokens';
import Storage from './auth-storage';
import TokenService from './token';
import { UserType } from '@/types';
import { get, parseRequestOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill';
import {
  fetchAssertionOptions,
} from '../../../../themes/paybis/assets/js/services/api-client/authentication-service-api-client';

export default {
  async startSession(email, locale, flowName = '', theme = null, isEmailProvided = false, sourceInfo = null, userType = UserType.PRIVATE) {
    const postBody = {
      email,
      locale,
      flow: flowName === 'widget' ? 'email-only' : 'email-phone',
    };

    if (theme !== null) {
      postBody.theme = theme;
    }
    if (sourceInfo !== null) {
      postBody.sourceInfo = sourceInfo;
    }

    const response = await client.startSession(postBody);
    const session = new Session(response, email, locale, flowName, theme, sourceInfo, userType);

    if (isEmailProvided) {
      session.setEmailProvided();
    }

    Storage.saveSession(session);

    return session;
  },

  async startRegistration(reCaptcha, session, phone, locale) {
    const sanitizedPhone = phone.replace(/\s/g, '');

    const { expirationTime, canBeResentAfter, attempts } = await client.startRegistration(session.sessionId, {
      phone: sanitizedPhone,
      locale,
      reCaptcha,
    });

    session.setPhoneOtpSession(expirationTime, canBeResentAfter, attempts, phone);

    Storage.saveSession(session);

    return session;
  },

  async startCorporateRegistration(reCaptcha, session, companyName, phone, locale) {
    const sanitizedPhone = phone.replace(/\s/g, '');
    const sanitizedCompanyName = companyName.trim();

    const { expirationTime, canBeResentAfter, attempts } = await client.startCorporateRegistration(session.sessionId, {
      companyName: sanitizedCompanyName,
      phone: sanitizedPhone,
      locale,
      reCaptcha,
    });

    session.setPhoneOtpSession(expirationTime, canBeResentAfter, attempts, phone);

    Storage.saveSession(session);

    return session;
  },

  async confirmEmailOtp(session, otp) {
    const response = await client.confirmEmailOtp(session.sessionId, {
      otp,
      isTosAccepted: true,
      channel: 'email',
    });

    session.confirmEmailOtpSession();

    if (response.code === 'registration-required') {
      Storage.saveSession(session);
      return null;
    }

    if (response.code === 'phone-confirmation-required') {
      session.setAdditionalPhoneAuth();
      Storage.saveSession(session);
      return null;
    }

    Storage.clearStoredSession();

    if (typeof window !== 'undefined' && response.language) {
      window.userLanguage = response.language;
    }

    return new Tokens(response);
  },

  async confirmPhoneOtp(session, otp, isTosAccepted) {
    const response = await client.confirmPhoneOtp(session.sessionId, {
      otp,
      isTosAccepted,
      channel: 'phone',
    });

    Storage.clearStoredSession();

    return new Tokens(response);
  },

  async resendEmailOtp(session, reCaptcha) {
    const userEmail = session.getEmailOtpSession().getContext();
    try {
      const { expirationTime, canBeResentAfter, attempts } = await client.resendOtp(session.sessionId, { reCaptcha });
      session.setEmailOtpSession(expirationTime, canBeResentAfter, attempts, userEmail);
    } catch (error) {
      if (error.code !== 404) {
        throw error;
      }
      session = await this.startSession(
        userEmail,
        session.getLocale(),
        session.getFlow(),
        session.getTheme(),
        session.isEmailProvided(),
        session.getSourceInfo(),
      );
    }

    Storage.saveSession(session);
  },

  async resendPhoneOtp(session, reCaptcha) {
    const { expirationTime, canBeResentAfter, attempts } = await client.resendOtp(session.sessionId, { reCaptcha });
    const phoneNumber = session.getPhoneOtpSession().getContext();
    session.setPhoneOtpSession(expirationTime, canBeResentAfter, attempts, phoneNumber);

    Storage.saveSession(session);
  },

  async logout() {
    await client.logout();
    TokenService.removeTokens();
  },

  async loginUsingPasskey() {
    const { data } = await fetchAssertionOptions();
    const cro = await get(parseRequestOptionsFromJSON({ publicKey: data }));
    const response = await client.loginUsingPasskeys(cro);

    return new Tokens(response);
  },
};
